import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../DashboardLayout/DasboardLayout";
import { API_URL } from "../config";
import { 
  MapPin, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Download,
  RefreshCw,
  Calendar,
  Filter,
  BarChart3,
  PieChart,
  Navigation,
  Car,
  Users,
  Globe,
  Target,
  Route,
  AlertCircle
} from "lucide-react";

const GeographicReport = () => {
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rideshareRequests, setRideshareRequests] = useState([]);
  const [rideshareRoutes, setRideshareRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("all");
  const [expandedSections, setExpandedSections] = useState({});

  // Get date range based on filter
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case "today":
        return {
          start: today,
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "7days":
        return {
          start: new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "14days":
        return {
          start: new Date(today.getTime() - 13 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "30days":
        return {
          start: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      default:
        return null;
    }
  };

  // Filter trips based on date range
  const filterTripsByDate = (trips, dateRange) => {
    if (!dateRange) return trips;
    
    return trips.filter(trip => {
      if (!trip.order_start_datetime) return false;
      const tripDate = new Date(trip.order_start_datetime);
      return tripDate >= dateRange.start && tripDate <= dateRange.end;
    });
  };

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [tripsRes, driversRes, customersRes, rideshareRequestsRes, rideshareRoutesRes] = await Promise.all([
        fetch(`${API_URL}/trip/`),
        fetch(`${API_URL}/driver/`),
        fetch(`${API_URL}/customerdetails/`),
        fetch(`${API_URL}/rideshare/requests/all`),
        fetch(`${API_URL}/rideshare/trips`)
      ]);

      const [tripsData, driversData, customersData, rideshareRequestsData, rideshareRoutesData] = await Promise.all([
        tripsRes.json(),
        driversRes.json(),
        customersRes.json(),
        rideshareRequestsRes.json(),
        rideshareRoutesRes.json()
      ]);

      setTrips(tripsData || []);
      setDrivers(driversData || []);
      setCustomers(customersData || []);
      setRideshareRequests(Array.isArray(rideshareRequestsData) ? rideshareRequestsData : []);
      setRideshareRoutes(Array.isArray(rideshareRoutesData) ? rideshareRoutesData : []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Helper function to extract city from location string
  const extractCity = (location) => {
    if (!location || location === 'Unknown') return 'Unknown';
    
    // Try to extract city from common patterns
    const patterns = [
      /,\s*([^,]+)$/, // Last part after comma
      /([^,]+),/, // First part before comma
      /^(.+?),/, // Beginning until first comma
    ];
    
    for (const pattern of patterns) {
      const match = location.match(pattern);
      if (match && match[1]) {
        const city = match[1].trim();
        if (city.length > 2 && city.length < 30) return city;
      }
    }
    
    return location.split(',')[0]?.trim() || 'Unknown';
  };

  // Process geographic data
  const geographicAnalysis = useMemo(() => {
    const dateRange = getDateRange(timeFilter);
    const filteredTrips = filterTripsByDate(trips, dateRange);

    // Rideshare revenue lives on `rideshare_requests` rows (one per passenger), not on `/trip`.
    // The pickup/dropoff for a request comes from the route it's on (`rideshare_trips`), whose
    // `origin_name`/`destination_name` are its analogs of pickup/dropoff address; the driver is
    // also on the route, not the request. Filter requests by `created_at` since they don't have
    // `order_start_datetime`.
    const routeByRideshareId = {};
    rideshareRoutes.forEach(route => {
      routeByRideshareId[route.rideshare_id] = route;
    });

    const filteredRideshareRequests = (dateRange
      ? rideshareRequests.filter(req => {
          if (!req.created_at) return false;
          const d = new Date(req.created_at);
          return d >= dateRange.start && d <= dateRange.end;
        })
      : rideshareRequests
    ).filter(req => (parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0) > 0);

    // Analyze by pickup location
    const pickupLocations = {};
    const dropoffLocations = {};
    const routeAnalysis = {};
    const cityPerformance = {};

    // Shared accumulator so private trips and rideshare requests land in the same buckets.
    const accumulate = (pickup, dropoff, revenue, driverId, customerId) => {
      // Pickup location analysis
      if (!pickupLocations[pickup]) {
        pickupLocations[pickup] = {
          count: 0,
          revenue: 0,
          avgRevenue: 0,
          drivers: new Set(),
          customers: new Set()
        };
      }
      pickupLocations[pickup].count++;
      pickupLocations[pickup].revenue += revenue;
      if (driverId) pickupLocations[pickup].drivers.add(driverId);
      if (customerId) pickupLocations[pickup].customers.add(customerId);

      // Dropoff location analysis
      if (!dropoffLocations[dropoff]) {
        dropoffLocations[dropoff] = {
          count: 0,
          revenue: 0,
          avgRevenue: 0,
          drivers: new Set(),
          customers: new Set()
        };
      }
      dropoffLocations[dropoff].count++;
      dropoffLocations[dropoff].revenue += revenue;
      if (driverId) dropoffLocations[dropoff].drivers.add(driverId);
      if (customerId) dropoffLocations[dropoff].customers.add(customerId);

      // Route analysis (pickup -> dropoff)
      const route = `${pickup} → ${dropoff}`;
      if (!routeAnalysis[route]) {
        routeAnalysis[route] = {
          count: 0,
          revenue: 0,
          avgRevenue: 0
        };
      }
      routeAnalysis[route].count++;
      routeAnalysis[route].revenue += revenue;

      // City/area performance (extract city from location if possible)
      const pickupCity = extractCity(pickup);
      const dropoffCity = extractCity(dropoff);

      [pickupCity, dropoffCity].forEach(city => {
        if (city && city !== 'Unknown') {
          if (!cityPerformance[city]) {
            cityPerformance[city] = {
              pickups: 0,
              dropoffs: 0,
              revenue: 0,
              avgRevenue: 0,
              totalTrips: 0
            };
          }
          cityPerformance[city].totalTrips++;
          cityPerformance[city].revenue += revenue;
          if (city === pickupCity) cityPerformance[city].pickups++;
          if (city === dropoffCity) cityPerformance[city].dropoffs++;
        }
      });
    };

    filteredTrips.forEach(trip => {
      const revenue = parseFloat(trip.delivery_cost_proposed) || 0;
      const pickup = trip.pickup_location || trip.pickup_address || 'Unknown Pickup';
      const dropoff = trip.dropoff_location || trip.dropoff_address || 'Unknown Dropoff';
      accumulate(pickup, dropoff, revenue, trip.driver_id, trip.cust_id);
    });

    filteredRideshareRequests.forEach(req => {
      const revenue = parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0;
      const route = routeByRideshareId[req.rideshare_id];
      const pickup = route?.origin_name || 'Unknown Pickup';
      const dropoff = route?.destination_name || 'Unknown Dropoff';
      accumulate(pickup, dropoff, revenue, route?.driver_id, req.passenger_id);
    });

    // Calculate averages
    Object.keys(pickupLocations).forEach(location => {
      const data = pickupLocations[location];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
      data.uniqueDrivers = data.drivers.size;
      data.uniqueCustomers = data.customers.size;
      delete data.drivers;
      delete data.customers;
    });

    Object.keys(dropoffLocations).forEach(location => {
      const data = dropoffLocations[location];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
      data.uniqueDrivers = data.drivers.size;
      data.uniqueCustomers = data.customers.size;
      delete data.drivers;
      delete data.customers;
    });

    Object.keys(routeAnalysis).forEach(route => {
      const data = routeAnalysis[route];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    Object.keys(cityPerformance).forEach(city => {
      const data = cityPerformance[city];
      data.avgRevenue = data.totalTrips > 0 ? data.revenue / data.totalTrips : 0;
    });

    return {
      filteredTrips,
      filteredRideshareRequests,
      pickupLocations,
      dropoffLocations,
      routeAnalysis,
      cityPerformance,
      totalTrips: filteredTrips.length + filteredRideshareRequests.length,
      totalRevenue:
        filteredTrips.reduce((sum, trip) => sum + (parseFloat(trip.delivery_cost_proposed) || 0), 0) +
        filteredRideshareRequests.reduce((sum, req) => sum + (parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0), 0)
    };
  }, [trips, rideshareRequests, rideshareRoutes, timeFilter]);

  // Get top performers
  const getTopPerformers = (data, metric, limit = 20) => {
    return Object.entries(data)
      .map(([name, stats]) => ({ name, ...stats }))
      .sort((a, b) => b[metric] - a[metric])
      .slice(0, limit);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getFilterLabel = (filter) => {
    switch(filter) {
      case "today": return "Today";
      case "7days": return "Past 7 Days";
      case "14days": return "Past 14 Days";
      case "30days": return "Past 30 Days";
      default: return "All Time";
    }
  };

  const exportData = (data, filename) => {
    const csv = [
      ['Location', 'Trips', 'Revenue', 'Average Revenue', 'Unique Drivers', 'Unique Customers'],
      ...data.map(item => [
        item.name,
        item.count || item.totalTrips || 0,
        item.revenue?.toFixed(2) || 0,
        item.avgRevenue?.toFixed(2) || 0,
        item.uniqueDrivers || 0,
        item.uniqueCustomers || 0
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <RefreshCw className="animate-spin text-blue-500" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  const { totalTrips, pickupLocations, dropoffLocations, routeAnalysis, cityPerformance, totalRevenue } = geographicAnalysis;

  // Get top performers
  const topPickupLocations = getTopPerformers(pickupLocations, 'revenue');
  const topDropoffLocations = getTopPerformers(dropoffLocations, 'revenue');
  const topRoutes = getTopPerformers(routeAnalysis, 'revenue');
  const topCities = getTopPerformers(cityPerformance, 'revenue');

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Geographic Performance Report
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Comprehensive geographic analysis of trips, revenue, and performance by location
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Globe className="text-gray-500" size={20} />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {getFilterLabel(timeFilter)}
              </span>
            </div>
          </div>

          {/* Time Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { value: "all", label: "All Time" },
              { value: "today", label: "Today" },
              { value: "7days", label: "Past 7 Days" },
              { value: "14days", label: "Past 14 Days" },
              { value: "30days", label: "Past 30 Days" }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setTimeFilter(filter.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  timeFilter === filter.value
                    ? "bg-blue-600 text-white shadow-lg transform scale-105"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Trips {timeFilter !== "all" ? `(${getFilterLabel(timeFilter)})` : "(All Time)"}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalTrips}
                </p>
              </div>
              <Activity className="text-purple-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Revenue {timeFilter !== "all" ? `(${getFilterLabel(timeFilter)})` : "(All Time)"}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${totalRevenue.toFixed(0)}
                </p>
              </div>
              <DollarSign className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Unique Locations</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Object.keys(pickupLocations).length + Object.keys(dropoffLocations).length}
                </p>
              </div>
              <MapPin className="text-blue-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Routes</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Object.keys(routeAnalysis).length}
                </p>
              </div>
              <Route className="text-orange-500" size={24} />
            </div>
          </div>
        </div>

        {/* Report Sections */}
        <div className="space-y-6">
          {/* Top Pickup Locations */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <MapPin className="mr-2 text-green-500" />
                Top Pickup Locations by Revenue
              </div>
              <button
                onClick={() => exportData(topPickupLocations, 'top-pickup-locations.csv')}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Trips</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Avg Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Drivers</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Customers</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {topPickupLocations.map((location, index) => (
                      <tr key={location.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          <div className="flex items-center">
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium mr-2">
                              #{index + 1}
                            </span>
                            {location.name}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{location.count}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">${location.revenue.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">${location.avgRevenue.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{location.uniqueDrivers}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{location.uniqueCustomers}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Top Dropoff Locations */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Target className="mr-2 text-blue-500" />
                Top Dropoff Locations by Revenue
              </div>
              <button
                onClick={() => exportData(topDropoffLocations, 'top-dropoff-locations.csv')}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Trips</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Avg Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Drivers</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Customers</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {topDropoffLocations.map((location, index) => (
                      <tr key={location.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          <div className="flex items-center">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium mr-2">
                              #{index + 1}
                            </span>
                            {location.name}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{location.count}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">${location.revenue.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">${location.avgRevenue.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{location.uniqueDrivers}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{location.uniqueCustomers}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Top Routes */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Route className="mr-2 text-orange-500" />
                Top Routes by Revenue
              </div>
              <button
                onClick={() => exportData(topRoutes, 'top-routes.csv')}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Route</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Trips</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Avg Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {topRoutes.map((route, index) => (
                      <tr key={route.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          <div className="flex items-center">
                            <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-medium mr-2">
                              #{index + 1}
                            </span>
                            <div className="max-w-md truncate" title={route.name}>
                              {route.name}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{route.count}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">${route.revenue.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">${route.avgRevenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* City Performance */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Globe className="mr-2 text-purple-500" />
                City Performance
              </div>
              <button
                onClick={() => exportData(topCities, 'city-performance.csv')}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">City</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total Trips</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Pickups</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Dropoffs</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Avg Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {topCities.map((city, index) => (
                      <tr key={city.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          <div className="flex items-center">
                            <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-medium mr-2">
                              #{index + 1}
                            </span>
                            {city.name}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{city.totalTrips}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{city.pickups}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{city.dropoffs}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">${city.revenue.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">${city.avgRevenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default GeographicReport;
